import { changedFields } from '../../utils/changedFields';
// 드롭 아이템 관리 탭 (CSV/TSV 자동 인식 + 무따옴표 공백 허용 버전)
import './ResourceTables.css';
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Typography,
  Tag,
  Tooltip,
  Modal,
  Alert,
  Radio,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SaveOutlined,
  CloseOutlined,
  DownloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { dropItemApi, dropItemSetApi, equipmentCategoryApi } from '../../services/dropItemApi';
import { entityApi } from '../../services/entityApi';
import type {
  DropItem,
  DropItemSet,
  WeaponCategory,
  ArmorCategory,
  AccessoryCategory
} from '../../services/dropItemApi';
import type { RaidEntity } from '../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;

// === CSV/TSV 공용 표준 헤더 ===
const CSV_HEADERS = [
  'item_key', 'item_name', 'drop_from_entity', 'equipment_type', 'equipment_category_key', 'item_set_key',
  'stat_str', 'stat_dex', 'stat_vit', 'stat_int', 'stat_foc', 'stat_wiz',
  'stat_physic_attack_prof', 'stat_magic_spell_prof', 'stat_physic_block_prof', 'stat_physic_dodge_prof', 'stat_magic_resist_prof',
  'stat_physic_armor', 'stat_magic_armor', 'stat_extra_armor', 'stat_fire_armor', 'stat_frost_armor', 'stat_poison_armor', 'stat_shadow_armor', 'stat_electr_armor',
  'memo'
] as const;
type CsvHeader = typeof CSV_HEADERS[number];

// === 구분자 자동 감지 ===
const detectDelimiter = (text: string): ',' | '\t' => {
  const cleaned = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = cleaned.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return ',';
  const sample = lines.slice(0, 5);
  let commaCount = 0;
  let tabCount = 0;
  for (const ln of sample) {
    commaCount += (ln.match(/,/g) || []).length;
    tabCount += (ln.match(/\t/g) || []).length;
  }
  return tabCount > commaCount ? '\t' : ',';
};

// === CSV/TSV 라인 파서 (따옴표/무따옴표 모두 지원, 공백 보존) ===
const parseCSVLine = (line: string, delimiter: ',' | '\t'): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  // 캐리지 리턴 제거, 행 끝 공백만 제거(필드 내부 공백은 그대로)
  line = line.replace(/\r/g, '').replace(/\n/g, '').trimEnd();

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      // "" => 내부의 " 로 처리
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && ch === delimiter) {
      result.push(current);
      current = '';
      continue;
    }

    current += ch;
  }

  result.push(current);
  return result;
};

const DropItemTab: React.FC = () => {
  const [items, setItems] = useState<DropItem[]>([]);
  const [sets, setSets] = useState<DropItemSet[]>([]);
  const [entities, setEntities] = useState<RaidEntity[]>([]);
  const [weaponCategories, setWeaponCategories] = useState<WeaponCategory[]>([]);
  const [armorCategories, setArmorCategories] = useState<ArmorCategory[]>([]);
  const [accessoryCategories, setAccessoryCategories] = useState<AccessoryCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode,setViewMode] = useState<'summary'|'full'>('summary');
  const [searchText,setSearchText] = useState('');
  const [page,setPage] = useState(1);
  const [pageSize,setPageSize] = useState(20);
  const [saving,setSaving]=useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<DropItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 템레벨 계산 함수
  const calculateItemLevel = (item: any): number => {
    if (!item) return 0;

    // 슬롯 계수 계산 (양손무기, 몸통방어구는 0.5)
    let slotFactor = 1;

    if (item.equipment_type === 'WEAPON') {
      const weaponCat = weaponCategories.find(c => c.key === item.equipment_category_key);
      if (weaponCat?.slot === 'TwoHand') {
        slotFactor = 0.5;
      }
    } else if (item.equipment_type === 'ARMOR') {
      const armorCat = armorCategories.find(c => c.key === item.equipment_category_key);
    }

    // 스탯 계산
    const baseStats = (item.stat_str || 0) + (item.stat_dex || 0) +
      (item.stat_vit || 0) + (item.stat_int || 0) +
      (item.stat_foc || 0) + (item.stat_wiz || 0);

    const attackProf = (item.stat_physic_attack_prof || 0) + (item.stat_magic_spell_prof || 0);
    const defenseProf = (item.stat_physic_block_prof || 0) + (item.stat_physic_dodge_prof || 0) +
      (item.stat_magic_resist_prof || 0);

    const elementalResist = (item.stat_fire_armor || 0) + (item.stat_frost_armor || 0) +
      (item.stat_poison_armor || 0) + (item.stat_shadow_armor || 0) +
      (item.stat_electr_armor || 0);

    const magicArmor = item.stat_magic_armor || 0;
    const extraArmor = (item as any).stat_extra_armor || 0; // 추가 방어도
    const sockets = (item as any).socket_count || (item as any).sockets || 0; // 소켓

    const itemLevel = slotFactor * (
      baseStats * 2 +
      attackProf * 4 +
      defenseProf * 2 +
      elementalResist / 10 +
      magicArmor / 5 +
      extraArmor / 5 +
      sockets * 0.4
    );

    return Math.round(itemLevel * 100) / 100; // 소수점 2자리
  };

  // 데이터 로드
  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsData, setsData, entitiesData, weaponData, armorData, accessoryData] = await Promise.all([
        dropItemApi.getAll(),
        dropItemSetApi.getAll(),
        entityApi.getAll(),
        equipmentCategoryApi.getWeaponCategories(),
        equipmentCategoryApi.getArmorCategories(),
        equipmentCategoryApi.getAccessoryCategories()
      ]);
      setItems(itemsData);
      setSets(setsData);
      setEntities(entitiesData);
      setWeaponCategories(weaponData);
      setArmorCategories(armorData);
      setAccessoryCategories(accessoryData);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      message.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // CSV 내보내기 (CSV_HEADERS 사용)
  const exportToCSV = () => {
    const headers = [...CSV_HEADERS];
    
    // drop_from_entity가 한글 이름으로 저장되어 있어도 코드로 변환하여 내보내기
    const resolveEntityCodeForExport = (value: any): string => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (!str) return '';
      const byCode = entities.find(e => e.entity_code === str);
      if (byCode) return byCode.entity_code;
      const byName = entities.find(e => e.entity_name === str);
      if (byName) return byName.entity_code;
      return str;
    };

    const csvContent = [
      headers.join(','),
      ...items.map(item =>
        headers.map((header) => {
          let value = (item as any)[header as CsvHeader];
          if (header === 'drop_from_entity') {
            value = resolveEntityCodeForExport(value);
          }
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `drop_items_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('CSV 파일이 다운로드되었습니다.');
  };

  // 카테고리 풀 얻기
  const getCategoryPoolByType = (type?: string | null) => {
    if (type === 'WEAPON') return weaponCategories;
    if (type === 'ARMOR') return armorCategories;
    if (type === 'ACCESSORY') return accessoryCategories;
    return [];
  };

  // 이름/키/라벨("이름 (key)") 무엇이 와도 key 찾아내기
  const resolveCategoryKeyLoose = (valueRaw: string, preferredType?: string | null) => {
    const value = (valueRaw || '').trim();
    if (!value) return { key: null as string | null, type: preferredType || null };

    // 1) 라벨 형태 "이름 (key)" -> 괄호 안 key 우선
    const labelMatch = value.match(/\(([^)]+)\)\s*$/);
    if (labelMatch?.[1]) {
      const keyGuess = labelMatch[1].trim();
      for (const [type, pool] of [
        ['WEAPON', weaponCategories],
        ['ARMOR', armorCategories],
        ['ACCESSORY', accessoryCategories],
      ] as const) {
        if (pool.find(c => c.key === keyGuess)) {
          return { key: keyGuess, type };
        }
      }
    }

    // 2) type 우선 풀에서 key 정합성 확인
    if (preferredType) {
      const pool = getCategoryPoolByType(preferredType);
      const byExactKey = pool.find(c => c.key === value);
      if (byExactKey) return { key: byExactKey.key, type: preferredType };

      const byExactName = pool.find(c => (c as any).name === value);
      if (byExactName) return { key: byExactName.key, type: preferredType };

      const vFlat = value.replace(/\s/g, '').toLowerCase();
      const loose = pool.filter(
        c =>
          (c as any).name?.replace(/\s/g, '').toLowerCase().includes(vFlat) ||
          c.key.toLowerCase().includes(vFlat)
      );
      if (loose.length === 1) return { key: loose[0].key, type: preferredType };
    }

    // 3) 모든 풀에서 검색 (유일 매칭이면 type 교정까지)
    const all: Array<{ type: 'WEAPON' | 'ARMOR' | 'ACCESSORY'; key: string; name?: string }> = [];
    weaponCategories.forEach(c => all.push({ type: 'WEAPON', key: c.key, name: (c as any).name }));
    armorCategories.forEach(c => all.push({ type: 'ARMOR', key: c.key, name: (c as any).name }));
    accessoryCategories.forEach(c => all.push({ type: 'ACCESSORY', key: c.key, name: (c as any).name }));

    const byKey = all.filter(c => c.key === value);
    if (byKey.length === 1) return { key: byKey[0].key, type: byKey[0].type };

    const byName = all.filter(c => (c.name || '') === value);
    if (byName.length === 1) return { key: byName[0].key, type: byName[0].type };

    const vFlat = value.replace(/\s/g, '').toLowerCase();
    const looseAll = all.filter(
      c =>
        (c.name || '').replace(/\s/g, '').toLowerCase().includes(vFlat) ||
        c.key.toLowerCase().includes(vFlat)
    );
    if (looseAll.length === 1) return { key: looseAll[0].key, type: looseAll[0].type };

    // 못 찾음
    return { key: null as string | null, type: preferredType || null };
  };

  // 드롭 몬스터도 이름으로 들어오면 코드로 바꿔주기
  const resolveEntityCodeLoose = (valueRaw: string) => {
    const value = (valueRaw || '').trim();
    if (!value) return null;
    const byCode = entities.find(e => e.entity_code === value);
    if (byCode) return byCode.entity_code;

    const byName = entities.filter(e => e.entity_name === value);
    if (byName.length === 1) return byName[0].entity_code;

    const vFlat = value.replace(/\s/g, '').toLowerCase();
    const loose = entities.filter(
      e =>
        e.entity_code.toLowerCase().includes(vFlat) ||
        (e.entity_name || '').replace(/\s/g, '').toLowerCase().includes(vFlat)
    );
    if (loose.length === 1) return loose[0].entity_code;

    return value; // 그대로 둠 (서버에서 실패하면 피드백)
  };

  // 아이템 1개 정규화: category key, 필요 시 type 교정, entity 코드 매핑
  const normalizeItemRefs = (raw: any): any => {
    const item = { ...raw };

    // entity
    if (item.drop_from_entity) {
      item.drop_from_entity = resolveEntityCodeLoose(item.drop_from_entity);
    }

    // category
    if (item.equipment_category_key) {
      const { key, type } = resolveCategoryKeyLoose(item.equipment_category_key, item.equipment_type);
      if (key) {
        item.equipment_category_key = key;
        if (type && item.equipment_type !== type) {
          // 타입까지 교정 (예: WEAPON + Shield -> ARMOR + ShieldKey)
          item.equipment_type = type;
        }
      }
    }

    return item;
  };

  // 서버 호출 전 검증: type별 풀에 key가 실제 존재하는지
  const validateItemAgainstReference = (item: any): string[] => {
    const errs: string[] = [];
    if (!item.item_key) errs.push('item_key 누락');
    if (!item.item_name) errs.push('item_name 누락');
    if (!item.equipment_type) errs.push('equipment_type 누락');
    if (!item.equipment_category_key) errs.push('equipment_category_key 누락');

    const pool = getCategoryPoolByType(item.equipment_type);
    const ok = !!pool.find(c => c.key === item.equipment_category_key);
    if (!ok) {
      errs.push(
        `유효하지 않은 카테고리 키: ${item.equipment_category_key} (type=${item.equipment_type})`
      );
    }
    return errs;
  };

  // CSV/TSV 가져오기 처리
  const handleCSVImport = async () => {
    if (!csvText.trim()) {
      message.error('CSV/TSV 데이터를 입력해주세요.');
      return;
    }

    setCsvImportLoading(true);
    try {
      const normalizedText = csvText
        .replace(/^\uFEFF/, '')
        .trim()
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

      const delimiter = detectDelimiter(normalizedText);
      const lines = normalizedText.split('\n').filter(line => line.trim() !== '');
      
      if (lines.length < 1) {
        message.error('올바른 형식이 아닙니다. 데이터가 없습니다.');
        setCsvImportLoading(false);
        return;
      }

      const firstLineFields = parseCSVLine(lines[0], delimiter).map(v => v.trim());
      const looksLikeHeader = firstLineFields.includes('item_key');

      const headers: string[] = looksLikeHeader ? firstLineFields : [...CSV_HEADERS];
      const startIndex = looksLikeHeader ? 1 : 0;

      if (!headers.includes('item_key')) {
        message.error('item_key 컬럼을 찾을 수 없습니다.');
        setCsvImportLoading(false);
        return;
      }

      const updates: any[] = [];
      const creates: any[] = [];
      const skipped: Array<{ key?: string; reason: string }> = [];

      for (let i = startIndex; i < lines.length; i++) {
        const raw = lines[i];
        if (!raw.trim()) continue;

        const values = parseCSVLine(raw, delimiter);
        let row = values.slice(0, headers.length);
        if (row.length < headers.length) {
          row = row.concat(Array(headers.length - row.length).fill(''));
        }

        const draft: Record<string, any> = {};
        headers.forEach((header, idx) => {
          const v = row[idx];
          if (v === '') draft[header] = null;
          else if (header.startsWith('stat_') || header === 'item_level') {
            const num = Number(v);
            draft[header] = Number.isFinite(num) ? num : 0;
          } else {
            draft[header] = v;
          }
        });

        // 🔧 참조 정규화 (이름/라벨 -> key, 필요 시 type 교정)
        const item = normalizeItemRefs(draft);

        // ✅ 검증
        const errs = validateItemAgainstReference(item);
        if (errs.length) {
          skipped.push({ key: item.item_key, reason: errs.join(', ') });
          continue;
        }

        const existing = items.find(it => it.item_key === item.item_key);
        if (existing) updates.push(item);
        else creates.push(item);
      }

      if (updates.length === 0 && creates.length === 0) {
        const msg = skipped.length
          ? `유효한 데이터가 없습니다. (스킵 ${skipped.length}건)`
          : '처리할 데이터가 없습니다.';
        message.warning(msg);
        if (skipped.length) {
          console.warn('스킵 사유:', skipped);
        }
        setCsvImportLoading(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const it of updates) {
        try {
          await dropItemApi.update(it.item_key, it);
          successCount++;
        } catch (e) {
          console.error(`Failed to update ${it.item_key}:`, e);
          failCount++;
        }
      }

      for (const it of creates) {
        try {
          await dropItemApi.create(it);
          successCount++;
        } catch (e) {
          console.error(`Failed to create ${it.item_key}:`, e);
          failCount++;
        }
      }

      const baseMsg = `처리 완료: ${successCount}개 성공, ${failCount}개 실패 (업데이트: ${updates.length}개, 신규: ${creates.length}개)`;
      const skipMsg = skipped.length ? `, 스킵: ${skipped.length}개` : '';
      message.success(baseMsg + skipMsg);

      if (skipped.length) {
        // 콘솔에 어떤 값이 왜 스킵됐는지 출력
        console.table(skipped);
        message.warning('일부 레코드는 카테고리/타입 불일치로 스킵되었습니다. 콘솔을 확인하세요.');
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      setCsvModalVisible(false);
      setCsvText('');
      await loadData();
    } catch (error) {
      console.error('CSV/TSV 가져오기 실패:', error);
      message.error('가져오기에 실패했습니다.');
    } finally {
      setCsvImportLoading(false);
    }
  };

  // 새 아이템 인라인 추가
  const startInlineCreate = () => {
    const newItem: DropItem = {
      item_key: '',
      item_name: '',
      drop_from_entity: entities[0]?.entity_code || '',
      equipment_type: 'WEAPON',
      equipment_category_key: '',
      item_set_key: null,
      stat_str: 0,
      stat_dex: 0,
      stat_vit: 0,
      stat_int: 0,
      stat_foc: 0,
      stat_wiz: 0,
      stat_physic_attack_prof: 0,
      stat_magic_spell_prof: 0,
      stat_physic_block_prof: 0,
      stat_physic_dodge_prof: 0,
      stat_magic_resist_prof: 0,
      stat_physic_armor: 0,
      stat_magic_armor: 0,
      // @ts-ignore - 서버 스키마에 새 필드 추가됨
      stat_extra_armor: 0,
      stat_fire_armor: 0,
      stat_frost_armor: 0,
      stat_poison_armor: 0,
      stat_shadow_armor: 0,
      stat_electr_armor: 0,
      memo: null
    };

    setPage(1);
    setIsCreating(true);
    setEditingKey('__new__');
    setEditingData(newItem);
  };

  // 인라인 편집 시작
  const startInlineEdit = (record: DropItem) => {
    setEditingKey(record.item_key);
    setEditingData({ ...record });
    setIsCreating(false);
  };

  // 인라인 편집 취소
  const cancelInlineEdit = () => {
    setEditingKey(null);
    setEditingData(null);
    setIsCreating(false);
  };

  // 인라인 편집 저장
  const saveInlineEdit = async () => {
    if (!editingData || saving) return;

    // 정규화 & 검증
    const normalized = isCreating ? normalizeItemRefs(editingData) : {...editingData};
    const errs = validateItemAgainstReference(normalized);
    if (errs.length) {
      message.error(`저장 실패: ${errs.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        await dropItemApi.create(normalized);
        message.success('아이템이 추가되었습니다.');
      } else {
        const original=items.find(item=>item.item_key===editingKey);
        if(!original) throw new Error('원본 아이템을 찾을 수 없습니다.');
        const patch=changedFields(original, normalized);
        if(Object.keys(patch).length) await dropItemApi.update(editingKey!,patch);
        message.success('아이템이 수정되었습니다.');
      }
      setEditingKey(null);
      setEditingData(null);
      setIsCreating(false);
      loadData();
    } catch (error) {
      console.error('저장 실패:', error);
      message.error('저장에 실패했습니다. 기존 항목과 입력 내용은 유지됩니다.');
    } finally { setSaving(false); }
  };

  // 필드 업데이트
  const updateField = (field: string, value: any) => {
    if (editingData) {
      const newData = { ...editingData, [field]: value };

      // equipment_type 변경 시 category 초기화
      if (field === 'equipment_type' && value !== editingData.equipment_type) {
        newData.equipment_category_key = '';
      }

      setEditingData(newData);
    }
  };

  // 카테고리 옵션 가져오기
  const getCategoryOptions = (equipType?: string) => {
    const type = equipType || editingData?.equipment_type;
    if (!type) return [];

    switch (type) {
      case 'WEAPON':
        return weaponCategories.map(cat => ({
          value: cat.key,
          label: `${cat.name} (${cat.key})`
        }));
      case 'ARMOR':
        return armorCategories.map(cat => ({
          value: cat.key,
          label: `${cat.name} (${cat.key})`
        }));
      case 'ACCESSORY':
        return accessoryCategories.map(cat => ({
          value: cat.key,
          label: `${cat.name} (${cat.key})`
        }));
      default:
        return [];
    }
  };

  // 삭제
  const handleDelete = async (itemKey: string) => {
    try {
      await dropItemApi.delete(itemKey);
      message.success('아이템이 삭제되었습니다.');
      loadData();
    } catch (error) {
      console.error('삭제 실패:', error);
      message.error('삭제에 실패했습니다.');
    }
  };

  // 선택 항목 일괄 삭제
  const handleBulkDelete = async () => {
    const keysToDelete = (selectedRowKeys || [])
      .filter((k): k is string => typeof k === 'string' && k !== '__new__')
      .filter(k => items.some(it => it.item_key === k));

    if (keysToDelete.length === 0) {
      message.warning('삭제할 항목이 없습니다.');
      return;
    }

    let successCount = 0;
    let failCount = 0;
    for (const key of keysToDelete) {
      try {
        await dropItemApi.delete(key);
        successCount++;
      } catch (e) {
        console.error('일괄 삭제 실패:', e);
        failCount++;
      }
    }

    setSelectedRowKeys([]);
    await loadData();
    message.success(`삭제 완료: ${successCount}건 성공, ${failCount}건 실패`);
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title: '키',
      dataIndex: 'item_key',
      key: 'item_key',
      width: 110,
      fixed: 'left' as const,
      sorter: (a: DropItem, b: DropItem) => (a.item_key || '').localeCompare(b.item_key || ''),
      filterSearch: true,
      onFilter: (value: any, record: DropItem) =>
        (record.item_key || '').toLowerCase().includes(value.toLowerCase()),
      render: (key: string, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <Input
            size="small"
            value={editingData?.item_key}
            onChange={(e) => updateField('item_key', e.target.value)}
            style={{ fontSize: '11px' }}
            onPressEnter={saveInlineEdit}
            placeholder="ITEM_XXX"
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{key}</Text>
        );
      }
    },
    {
      title: '이름',
      dataIndex: 'item_name',
      key: 'item_name',
      width: 120,
      fixed: 'left' as const,
      sorter: (a: DropItem, b: DropItem) => (a.item_name || '').localeCompare(b.item_name || ''),
      filterSearch: true,
      onFilter: (value: any, record: DropItem) =>
        (record.item_name || '').toLowerCase().includes(value.toLowerCase()),
      render: (name: string, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <Input
            size="small"
            value={editingData?.item_name}
            onChange={(e) => updateField('item_name', e.target.value)}
            style={{ fontSize: '11px' }}
            onPressEnter={saveInlineEdit}
            placeholder="아이템 이름"
          />
        ) : (
          <Text style={{ fontSize: '12px' }}>{name}</Text>
        );
      }
    },
    {
      title: '드롭',
      dataIndex: 'drop_from_entity',
      key: 'drop_from_entity',
      width: 80,
      sorter: (a: DropItem, b: DropItem) => (a.drop_from_entity || '').localeCompare(b.drop_from_entity || ''),
      filters: Array.from(new Set(items.map(item => item.drop_from_entity))).map(entity => ({
        text: entities.find(e => e.entity_code === entity)?.entity_name || entity,
        value: entity,
      })),
      onFilter: (value: any, record: DropItem) => record.drop_from_entity === value,
      render: (entityCode: string, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');

        if (isEditing) {
          return (
            <Select
              size="small"
              value={editingData?.drop_from_entity}
              onChange={(value) => updateField('drop_from_entity', value)}
              style={{ width: '100%', fontSize: '11px' }}
              showSearch
              optionFilterProp="children"
            >
              {entities.map(entity => (
                <Select.Option key={entity.entity_code} value={entity.entity_code}>
                  {entity.entity_name}
                </Select.Option>
              ))}
            </Select>
          );
        }

        const entity = entities.find(e => e.entity_code === entityCode);
        return (
          <Tooltip title={`${entity?.entity_name} (${entityCode})`}>
            <Text style={{ fontSize: '11px' }}>{entity?.entity_name || entityCode}</Text>
          </Tooltip>
        );
      }
    },
    {
      title: '타입',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      width: 60,
      filters: [
        { text: 'WEAPON', value: 'WEAPON' },
        { text: 'ARMOR', value: 'ARMOR' },
        { text: 'ACCESSORY', value: 'ACCESSORY' },
      ],
      onFilter: (value: any, record: DropItem) => record.equipment_type === value,
      render: (type: string, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');

        if (isEditing) {
          return (
            <Select
              size="small"
              value={editingData?.equipment_type}
              onChange={(value) => updateField('equipment_type', value)}
              style={{ width: '100%', fontSize: '11px' }}
            >
              <Select.Option value="WEAPON">WEAPON</Select.Option>
              <Select.Option value="ARMOR">ARMOR</Select.Option>
              <Select.Option value="ACCESSORY">ACCESSORY</Select.Option>
            </Select>
          );
        }

        const colors = {
          WEAPON: 'red',
          ARMOR: 'blue',
          ACCESSORY: 'green'
        };
        return <Tag color={colors[type as keyof typeof colors]} style={{ fontSize: '10px', margin: 0 }}>{type}</Tag>;
      }
    },
    {
      title: '카테고리',
      dataIndex: 'equipment_category_key',
      key: 'equipment_category_key',
      width: 90,
      render: (categoryKey: string, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');

        if (isEditing) {
          const options = getCategoryOptions();
          return (
            <Select
              size="small"
              value={editingData?.equipment_category_key}
              onChange={(value) => updateField('equipment_category_key', value)}
              style={{ width: '100%', fontSize: '11px' }}
              placeholder="선택"
              showSearch
              optionFilterProp="children"
            >
              {options.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          );
        }

        let category: any = null;
        if (record.equipment_type === 'WEAPON') {
          category = weaponCategories.find(c => c.key === categoryKey);
        } else if (record.equipment_type === 'ARMOR') {
          category = armorCategories.find(c => c.key === categoryKey);
        } else if (record.equipment_type === 'ACCESSORY') {
          category = accessoryCategories.find(c => c.key === categoryKey);
        }

        return <Text style={{ fontSize: '11px' }}>{category?.name || categoryKey}</Text>;
      }
    },
    {
      title: '세트',
      dataIndex: 'item_set_key',
      key: 'item_set_key',
      width: 80,
      render: (setKey: string | null, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');

        if (isEditing) {
          return (
            <Select
              size="small"
              value={editingData?.item_set_key}
              onChange={(value) => updateField('item_set_key', value)}
              style={{ width: '100%', fontSize: '11px' }}
              placeholder="없음"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {sets.map(set => (
                <Select.Option key={set.set_key} value={set.set_key}>
                  {set.set_name}
                </Select.Option>
              ))}
            </Select>
          );
        }

        if (!setKey) return <Text style={{ fontSize: '11px' }}>-</Text>;
        const set = sets.find(s => s.set_key === setKey);
        return <Tag color="purple" style={{ fontSize: '10px', margin: 0 }}>{set?.set_name || setKey}</Tag>;
      }
    },
    {
      title: '템렙',
      key: 'item_level',
      width: 50,
      sorter: (a: DropItem, b: DropItem) => calculateItemLevel(a) - calculateItemLevel(b),
      render: (_: any, record: DropItem) => {
        const level = calculateItemLevel(editingKey === (record.item_key || '__new__') ? editingData : record);
        return <Text strong style={{ fontSize: '11px', color: level > 100 ? '#faad14' : undefined }}>{level}</Text>;
      }
    },
    // 기본 스탯
    {
      title: '힘',
      dataIndex: 'stat_str',
      key: 'stat_str',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_str || 0) - (b.stat_str || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_str}
            onChange={(val) => updateField('stat_str', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '민',
      dataIndex: 'stat_dex',
      key: 'stat_dex',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_dex || 0) - (b.stat_dex || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_dex}
            onChange={(val) => updateField('stat_dex', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '체',
      dataIndex: 'stat_vit',
      key: 'stat_vit',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_vit || 0) - (b.stat_vit || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_vit}
            onChange={(val) => updateField('stat_vit', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '지',
      dataIndex: 'stat_int',
      key: 'stat_int',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_int || 0) - (b.stat_int || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_int}
            onChange={(val) => updateField('stat_int', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '집',
      dataIndex: 'stat_foc',
      key: 'stat_foc',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_foc || 0) - (b.stat_foc || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_foc}
            onChange={(val) => updateField('stat_foc', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '마',
      dataIndex: 'stat_wiz',
      key: 'stat_wiz',
      width: 45,
      sorter: (a: DropItem, b: DropItem) => (a.stat_wiz || 0) - (b.stat_wiz || 0),
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_wiz}
            onChange={(val) => updateField('stat_wiz', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    // 숙련도
    {
      title: '물공숙',
      dataIndex: 'stat_physic_attack_prof',
      key: 'stat_physic_attack_prof',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_physic_attack_prof}
            onChange={(val) => updateField('stat_physic_attack_prof', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '마공숙',
      dataIndex: 'stat_magic_spell_prof',
      key: 'stat_magic_spell_prof',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_magic_spell_prof}
            onChange={(val) => updateField('stat_magic_spell_prof', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '막기숙',
      dataIndex: 'stat_physic_block_prof',
      key: 'stat_physic_block_prof',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_physic_block_prof}
            onChange={(val) => updateField('stat_physic_block_prof', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '회피숙',
      dataIndex: 'stat_physic_dodge_prof',
      key: 'stat_physic_dodge_prof',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_physic_dodge_prof}
            onChange={(val) => updateField('stat_physic_dodge_prof', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '마저숙',
      dataIndex: 'stat_magic_resist_prof',
      key: 'stat_magic_resist_prof',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_magic_resist_prof}
            onChange={(val) => updateField('stat_magic_resist_prof', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    // 방어/저항
    {
      title: '물방',
      dataIndex: 'stat_physic_armor',
      key: 'stat_physic_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_physic_armor}
            onChange={(val) => updateField('stat_physic_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '마방',
      dataIndex: 'stat_magic_armor',
      key: 'stat_magic_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_magic_armor}
            onChange={(val) => updateField('stat_magic_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '추가방',
      dataIndex: 'stat_extra_armor',
      key: 'stat_extra_armor',
      width: 55,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={(editingData as any)?.stat_extra_armor}
            onChange={(val) => updateField('stat_extra_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '화저',
      dataIndex: 'stat_fire_armor',
      key: 'stat_fire_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_fire_armor}
            onChange={(val) => updateField('stat_fire_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '냉저',
      dataIndex: 'stat_frost_armor',
      key: 'stat_frost_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_frost_armor}
            onChange={(val) => updateField('stat_frost_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '독저',
      dataIndex: 'stat_poison_armor',
      key: 'stat_poison_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_poison_armor}
            onChange={(val) => updateField('stat_poison_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '암저',
      dataIndex: 'stat_shadow_armor',
      key: 'stat_shadow_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_shadow_armor}
            onChange={(val) => updateField('stat_shadow_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '전저',
      dataIndex: 'stat_electr_armor',
      key: 'stat_electr_armor',
      width: 45,
      render: (value: number, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <InputNumber
            size="small"
            value={editingData?.stat_electr_armor}
            onChange={(val) => updateField('stat_electr_armor', val)}
            style={{ width: '100%', fontSize: '11px' }}
            min={0}
          />
        ) : (
          <Text style={{ fontSize: '11px' }}>{value || 0}</Text>
        );
      }
    },
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      width: 100,
      ellipsis: true,
      render: (memo: string | null, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');
        return isEditing ? (
          <Input
            size="small"
            value={editingData?.memo || ''}
            onChange={(e) => updateField('memo', e.target.value || null)}
            style={{ fontSize: '11px' }}
            placeholder="메모"
          />
        ) : (
          <Tooltip title={memo}>
            <Text style={{ fontSize: '11px' }} ellipsis>{memo || '-'}</Text>
          </Tooltip>
        );
      }
    },
    {
      title: '액션',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: DropItem) => {
        const isEditing = editingKey === (record.item_key || '__new__');

        if (isEditing) {
          return (
            <Space size={2}>
              <Button
                type="link"
                size="small"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={saveInlineEdit}
                style={{ padding: '0 4px' }}
              />
              <Button
                type="link"
                size="small"
                danger
                icon={<CloseOutlined />}
                disabled={saving}
                onClick={cancelInlineEdit}
                style={{ padding: '0 4px' }}
              />
            </Space>
          );
        }

        return (
          <Space size={2}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => startInlineEdit(record)}
              disabled={editingKey !== null}
              style={{ padding: '0 4px' }}
            />
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              onConfirm={() => handleDelete(record.item_key)}
              okText="삭제"
              cancelText="취소"
              disabled={editingKey !== null}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey !== null}
                style={{ padding: '0 4px' }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];


  const statLabels: Record<string, string> = {
    stat_str: '힘',
    stat_dex: '민첩',
    stat_vit: '체력',
    stat_int: '지능',
    stat_foc: '집중',
    stat_wiz: '지혜',
    stat_physic_attack_prof: '물리 공격 숙련',
    stat_magic_spell_prof: '마법 시전 숙련',
    stat_physic_block_prof: '방어 숙련',
    stat_physic_dodge_prof: '회피 숙련',
    stat_magic_resist_prof: '마법 저항 숙련',
    stat_physic_armor: '물리 방어',
    stat_magic_armor: '마법 방어',
    stat_extra_armor: '추가 방어',
    stat_fire_armor: '화염 저항',
    stat_frost_armor: '냉기 저항',
    stat_poison_armor: '독 저항',
    stat_shadow_armor: '암흑 저항',
    stat_electr_armor: '번개 저항',
  };
  const summaryColumns = [
    {
      title: '아이템',
      key: 'item_name',
      width: 240,
      render: (_: any, r: DropItem) => <Text strong>{r.item_name}</Text>,
    },
    {
      title: '드롭 몬스터',
      key: 'owner',
      width: 160,
      render: (_: any, r: DropItem) =>
        entities.find((e) => e.entity_code === r.drop_from_entity)?.entity_name ||
        r.drop_from_entity ||
        '미설정',
    },
    {
      title: '장비',
      key: 'equipment',
      width: 160,
      render: (_: any, r: DropItem) => (
        <Space direction="vertical" size={0}>
          <Text>
            {{ WEAPON: '무기', ARMOR: '방어구', ACCESSORY: '장신구' }[
              r.equipment_type
            ] || r.equipment_type}
          </Text>
          <Text type="secondary">
            {[
              ...weaponCategories,
              ...armorCategories,
              ...accessoryCategories,
            ].find((c) => c.key === r.equipment_category_key)?.name ||
              r.equipment_category_key}
          </Text>
        </Space>
      ),
    },
    {
      title: '아이템 레벨',
      key: 'level',
      width: 110,
      render: (_: any, r: DropItem) => calculateItemLevel(r),
    },
    {
      title: '세트',
      key: 'set',
      width: 160,
      render: (_: any, r: DropItem) =>
        sets.find((x) => x.set_key === r.item_set_key)?.set_name ||
        r.item_set_key ||
        '없음',
    },
    columns.find((c) => c.key === 'action')!,
  ];
  const fullView = viewMode === 'full' || editingKey !== null;
  const matchingItems = items.filter((r) =>
    [
      r.item_key,
      r.item_name,
      r.memo,
      entities.find((e) => e.entity_code === r.drop_from_entity)?.entity_name,
    ].some((v) => v?.toLowerCase().includes(searchText.toLowerCase())),
  );

  // 편집 중인 데이터를 포함한 테이블 데이터
  const tableData = isCreating && editingData ? [{...editingData,item_key:''}, ...matchingItems] : matchingItems;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>드롭 아이템</Title>
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToCSV}
            disabled={editingKey !== null}
          >
            CSV 내보내기
          </Button>
          <Popconfirm
            title={`선택된 ${selectedRowKeys.length}개를 삭제하시겠습니까?`}
            onConfirm={handleBulkDelete}
            okText="삭제"
            cancelText="취소"
            disabled={editingKey !== null || selectedRowKeys.length === 0}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={editingKey !== null || selectedRowKeys.length === 0}
            >
              선택 삭제
            </Button>
          </Popconfirm>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setCsvModalVisible(true)}
            disabled={editingKey !== null}
          >
            CSV/TSV 가져오기
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            disabled={editingKey !== null}
          >
            새로고침
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={startInlineCreate}
            disabled={editingKey !== null}
          >
            아이템 추가
          </Button>
        </Space>
      </div>

      <Space wrap className="resource-filters">
        <Input.Search aria-label="드롭 아이템 검색" allowClear placeholder="아이템 · 몬스터 · 코드 검색" value={searchText} disabled={editingKey!==null} onChange={e=>{setSearchText(e.target.value);setPage(1);}} style={{width:280}}/>
        <Radio.Group aria-label="아이템 표시 방식" value={fullView?'full':'summary'} disabled={editingKey!==null} onChange={e=>setViewMode(e.target.value)} optionType="button" options={[{value:'summary',label:'간단히'},{value:'full',label:'전체 능력치'}]}/>
        <Text type="secondary">{editingKey!==null?'편집 중 · 저장 또는 취소하면 목록으로 돌아갑니다.':fullView?'모든 능력치 표시 · 좌우로 스크롤해 확인하세요.':'행을 펼치면 모든 능력치와 메모를 확인할 수 있습니다.'}</Text>
      </Space>
      <Table
        className={fullView?'':'resource-table'}
        columns={fullView?columns:summaryColumns}
        expandable={fullView?undefined:{expandedRowRender:(r:DropItem)=><div className="resource-details"><Text code copyable>{r.item_key}</Text><Descriptions size="small" column={{xs:1,sm:2,md:3,lg:4}} style={{marginTop:16}} items={Object.entries(statLabels).map(([key,label])=>({key,label,children:(r as any)[key] ?? '미설정'}))}/><p>{r.memo || '메모 없음'}</p></div>}}
        dataSource={tableData}
        rowKey={(record) => record.item_key || '__new__'}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
        }}
        loading={loading || saving}
        scroll={{ x: fullView?2200:950 }}
        pagination={{
          disabled: editingKey !== null,
          current: page,
          pageSize,
          onChange:(next,size)=>{setPage(next);setPageSize(size);},
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (total) => `총 ${total}개`
        }}
        size="small"
        rowClassName={(record) => {
          const isEditing = editingKey === (record.item_key || '__new__');
          return isEditing ? 'editing-row' : '';
        }}
      />

      {/* CSV/TSV 가져오기 모달 */}
      <Modal
        title="CSV/TSV 데이터 가져오기"
        open={csvModalVisible}
        onOk={handleCSVImport}
        onCancel={() => {
          setCsvModalVisible(false);
          setCsvText('');
        }}
        width={800}
        confirmLoading={csvImportLoading}
        okText="가져오기"
        cancelText="취소"
      >
        <Alert
          message="붙여넣기 형식 안내"
          description={
            <div>
              <p>• <b>CSV(쉼표)</b> 또는 <b>TSV(탭, 엑셀에서 셀 범위 복사)</b>를 그대로 붙여넣으면 자동 인식합니다.</p>
              <p>• 헤더(<code style={{ fontSize: '11px' }}>item_key,item_name,...,memo</code>)가 없으면 아래 표준 헤더로 처리합니다.</p>
              <code style={{ fontSize: '11px', display: 'block', whiteSpace: 'pre-wrap' }}>
                {CSV_HEADERS.join(',')}
              </code>
              <p style={{ marginTop: 8 }}>
                • 값에 <b>쉼표</b>가 들어가면 CSV에서는 구분자로 인식되므로, <b>탭(엑셀 복사)</b> 붙여넣기를 권장합니다.<br />
                • 따옴표 없이 공백(스페이스)은 그대로 허용됩니다. (필드 구분은 쉼표/탭으로 합니다)
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <TextArea
          rows={15}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="여기에 CSV 또는 TSV 데이터를 붙여넣으세요..."
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        />
      </Modal>

      <style>{`
        .editing-row {
          background-color: rgba(24, 144, 255, 0.05) !important;
        }
        .editing-row:hover > td {
          background-color: rgba(24, 144, 255, 0.05) !important;
        }
      `}</style>
    </div>
  );
};

export default DropItemTab;
